import { Button as Btn } from '@shared/components/ui/shadcn/button';
import { cn } from '@shared/utils/utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  cva,
  type VariantProps as VariantProps_,
} from 'class-variance-authority';
import React from 'react';

export const buttonVariants = cva(
  'disabled:opacity-40 transition-colors flex items-center gap-[6px] text-sm font-medium',
  {
    variants: {
      size: {
        sm: 'h-8 px-3 py-[7px]',
        default: 'h-9 px-[14px] py-[9px]',
        lg: 'h-10 px-4 py-[11px]',
        icon: 'h-9 w-9',
      },
      variant: {
        primary:
          'text-btn-text bg-primary shadow hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring',
        secondary:
          'text-secondary-foreground bg-secondary hover:bg-secondary/80 focus-visible:ring-2 focus-visible:ring-ring',
        outline:
          'text-text bg-background border border-lines hover:bg-hover-press hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring',
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring',
        link: 'text-primary hover:text-primary/80 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring',
        icon: 'text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring',
      },
    },
  }
);

export type VariantProps = VariantProps_<typeof buttonVariants>;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps_<typeof buttonVariants> {
  asChild?: boolean;
}

interface CustomButtonProps extends ButtonProps {
  iconPosition?: 'start' | 'end';
  fullWidth?: boolean;
  loading?: boolean;
  textcolor?: string;
  bgcolor?: string;
  icon?: IconProp;
  classNameIcon?: string;
  name?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      bgcolor,
      loading,
      textcolor,
      fullWidth,
      children,
      asChild = false,
      iconPosition = 'start',
      variant = 'primary',
      icon,
      size,
      classNameIcon,
      className,
      name,
      ...props
    },
    ref
  ) => {
    const adjustedVariant = (() => {
      switch (variant) {
        case 'primary':
          return 'default';
        case 'icon':
          return 'default';
        default:
          return variant;
      }
    })();
    const testId = name?.split('.').join('-');

    // When rendering asChild (Slot), ensure a single child element is passed.
    // Icons are skipped so the caller can include any content inside the child element.
    const content = asChild
      ? children
      : (
        <>
          {icon && iconPosition === 'start' && (
            <FontAwesomeIcon
              icon={icon}
              className={cn(
                '!h-4 mr-0',
                variant === 'icon' && '!h-4',
                classNameIcon
              )}
            />
          )}
          {children}
          {icon && iconPosition === 'end' && (
            <FontAwesomeIcon
              icon={icon}
              className={cn(
                '!h-4 mr-0',
                variant === 'icon' && '!h-4',
                classNameIcon
              )}
            />
          )}
        </>
      );

    return (
      <Btn
        id={name}
        data-testid={testId ?? undefined}
        ref={ref}
        {...props}
        variant={adjustedVariant}
        size={size}
        className={cn(
          buttonVariants({ size, variant }),
          fullWidth && 'w-full',
          textcolor && `text-${textcolor}`,
          bgcolor && `bg-${bgcolor}`,
          className
        )}
        disabled={loading || props.disabled}
        asChild={asChild}
      >
        {content}
      </Btn>
    );
  }
);
